import { UseFormReturn } from 'react-hook-form';
import { NetworkField } from '../../Field/NetworkField';
import { useExternalOutgoingCryptoNetworks } from '../../../api/transfers';
import { useEffect, useMemo } from 'react';
import { FormTextField, Tab, TabsField } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { CurrencyField } from '../../Field/CurrencyField';
import { useAccountProductCryptoQuery, useAccountsQuery } from '../../../api/accounts';
import { useNewRegExp } from '../../../hooks/useNewRegExp';
import { CryptoBeneficiaryFormValues } from './types';
import { isCryptoCurrency } from '@razz-team/utils';
import { uniq } from 'lodash-es';

export function CryptoBeneficiaryFields({
  form,
}: {
  form: UseFormReturn<CryptoBeneficiaryFormValues>;
}) {
  const { t } = useTranslation();
  const subtype = form.watch('beneficiary.value.subtype', undefined);
  const currency = form.watch('beneficiary.value.currency', undefined);
  const network = form.watch('beneficiary.value.transferNetworkId', undefined);

  const accountsQuery = useAccountsQuery();
  const cryptoProductQuery = useAccountProductCryptoQuery();
  const currencies = useMemo(
    () =>
      uniq([
        ...(accountsQuery.data?.map((it) => it.balance.currency).filter(isCryptoCurrency) ?? []),
        ...(cryptoProductQuery.data?.product?.conditions.map((c) => c.currency) ?? []),
      ]),
    [accountsQuery.data, cryptoProductQuery.data],
  );

  const networksQuery = useExternalOutgoingCryptoNetworks({ currency });
  const networks = useMemo(
    () => networksQuery.data?.map(({ type }) => type) ?? [],
    [networksQuery.data],
  );
  const networkType = useMemo(
    () => networksQuery.data?.find((n) => n.type === network),
    [network, networksQuery.data],
  );
  const networkPattern = useNewRegExp(networkType?.pattern);
  const isDestinationTagAllowed = networkType?.isDestinationTagAllowed;

  useEffect(() => {
    let nextValue;
    if (networksQuery.isSuccess) {
      if (network && !networkType) {
        nextValue = null;
      } else if (!network && networks.length === 1) {
        nextValue = networks[0];
      }
    }

    if (nextValue !== undefined) {
      form.setValue('beneficiary.value.transferNetworkId', nextValue, {
        shouldValidate: false,
      });
    }
  }, [form, network, networkType, networks, networksQuery.isSuccess]);

  return (
    <>
      <TabsField name="beneficiary.value.subtype" label={t('Crypto recipient type')} required>
        <Tab label={t('Myself')} value="MYSELF" />
        <Tab label={t('Business')} value="BUSINESS" />
        <Tab label={t('Individual')} value="INDIVIDUAL" />
      </TabsField>
      <CurrencyField
        name="beneficiary.value.currency"
        label={t('Currency')}
        options={currencies}
        loading={cryptoProductQuery.isPending}
        autoFocus
        required
        disableClearable
      />
      <NetworkField
        name="beneficiary.value.transferNetworkId"
        label={t('Network')}
        options={networks}
        loading={networksQuery.isPending}
        helperText={t('Choosing the wrong network may result in a loss of funds.')}
        required
        disableClearable
      />
      <FormTextField
        name="beneficiary.value.accountNumber"
        label={t('Address')}
        placeholder={networkType?.mask}
        maxLength={networkType?.maxLength}
        pattern={
          networkPattern
            ? {
                value: networkPattern,
                message: t('Invalid address'),
              }
            : undefined
        }
        autoComplete="off"
        required
      />
      {isDestinationTagAllowed ? (
        <FormTextField
          name="beneficiary.value.destinationTag"
          label={t('Memo')}
          autoComplete="off"
        />
      ) : null}
      {!subtype || subtype === 'MYSELF' ? null : (
        <FormTextField
          name="beneficiary.value.beneficiaryName"
          label={t(subtype === 'BUSINESS' ? 'Business name' : `Individual name`)}
          required
          autoComplete="off"
        />
      )}

      <FormTextField
        name="beneficiary.value.displayName"
        label={t(`Label (optional)`)}
        autoComplete="off"
      />
    </>
  );
}
